<template>
    <div
        v-bind="attrs"
        :tabindex="searchable ? -1 : tabindex"
        :class="{
            'multiselect--active': isOpen,
            'multiselect--disabled': disabled || loading || optionsLoading,
            'multiselect--above': isAbove,
        }"
        class="multiselect"
        @focus="activate()"
        @blur="searchable ? false : deactivate()"
        @keydown.self.down.prevent="pointerForward()"
        @keydown.self.up.prevent="pointerBackward()"
        @keypress.enter.tab.stop.self="addPointerElement($event)"
        @keyup.esc="deactivate()"
    >
        <slot name="clear" :search="search"></slot>
        <div
            ref="tags"
            class="multiselect__tags form-control h-auto"
            :class="{ 'is-valid': state === true, 'is-invalid': state === false }"
        >
            <slot name="caret" :toggle="toggle">
                <div class="multiselect__select" @mousedown.prevent.stop="toggle()"></div>
            </slot>
            <slot
                name="selection"
                :search="search"
                :remove="removeElement"
                :values="visibleValues"
                :is-open="isOpen"
            >
                <div v-if="!showCheckboxes" v-show="visibleValues.length > 0" class="multiselect__tags-wrap">
                    <template v-for="(option, index) in visibleValues">
                        <slot name="tag" :option="option" :search="search" :remove="removeElement">
                            <span :key="index" class="multiselect__tag">
                                <span v-text="getOptionLabel(option)"></span>
                                <i
                                    aria-hidden="true"
                                    tabindex="1"
                                    class="multiselect__tag-icon"
                                    @keypress.enter.prevent="removeElement(option); onCloseEmitted()"
                                    @mousedown.prevent="removeElement(option); onCloseEmitted()"
                                ></i>
                            </span>
                        </slot>
                    </template>
                </div>
                <template v-if="showCheckboxes && visibleValues.length && !isOpen">
                    <span class="text-nowrap d-block overflow-hidden">{{ selectedOptionsLabel }} ({{ visibleValues.length }})</span>
                </template>
                <template v-if="internalValue && internalValue.length > limit && !isOpen">
                    <slot name="limit">
                        <span v-text="limitText(internalValue.length - limit)"></span>
                    </slot>
                </template>
            </slot>
            <transition name="multiselect__loading">
                <slot name="loading">
                    <div v-show="loading || optionsLoading" class="multiselect__spinner"></div>
                </slot>
            </transition>
            <input
                v-if="searchable"
                :id="id"
                ref="search"
                :name="name"
                type="text"
                autocomplete="nope"
                :placeholder="placeholder"
                :style="inputStyle"
                :value="search"
                :disabled="disabled || loading || optionsLoading"
                :tabindex="tabindex"
                class="multiselect__input"
                @input="updateSearch($event.target.value)"
                @focus.prevent="onSearchFocus()"
                @blur.prevent="onSearchBlur()"
                @keyup.esc="deactivate()"
                @keydown.down.prevent="pointerForward()"
                @keydown.up.prevent="pointerBackward()"
                @keypress.enter.prevent.stop.self="addPointerElement($event)"
                @keydown.delete.stop="removeLastElement()"
            >
            <span v-if="isSingleLabelVisible" class="multiselect__single" @mousedown.prevent="toggle">
                <slot name="singleLabel" :option="singleValue">
                    {{ currentOptionLabel }}
                </slot>
            </span>
            <span
                v-if="isPlaceholderVisible"
                class="multiselect__placeholder nowrap"
                @mousedown.prevent="toggle"
            >
                <slot name="placeholder">
                    {{ placeholder }}
                </slot>
            </span>
        </div>
        <transition name="multiselect">
            <div
                v-show="isOpen"
                class="multiselect__content-container"
                :class="{
                    'multiselect__content-container--checkboxes': showCheckboxes,
                }"
            >
                <div
                    v-if="showCheckboxes"
                    class="multiselect__select-all multiselect__select-all--main"
                    @focus.stop="activate()"
                    @mousedown.prevent
                    @click="search ? toggleAllFiltered() : toggleAll()"
                >
                    <div>
                        <div class="custom-control custom-checkbox">
                            <input
                                :checked="checkedAllFiltered"
                                :indeterminate.prop="indeterminateAllFiltered"
                                :disabled="!filteredOptionsWithoutLabels.length"
                                type="checkbox"
                                class="custom-control-input"
                            >
                            <div class="custom-control-label text-white">
                                <slot name="selectAllHeader" :values="visibleValues">
                                    {{
                                        checkedAllFiltered
                                            ? $t('deselectAllVisible')
                                            : $t('selectAllVisible')
                                    }}
                                </slot>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="showCheckboxes" class="multiselect__search-inside">
                    <transition name="multiselect__loading">
                        <slot name="loadingInside">
                            <div
                                v-show="loading || isDebouncedOptionsLoading || isTyping"
                                class="multiselect__spinner"
                            ></div>
                        </slot>
                    </transition>
                    <feather type="search" class="multiselect__search-inside-icon text-muted flex-shrink-0" />
                    <input
                        v-if="searchable"
                        ref="searchInside"
                        type="text"
                        autocomplete="nope"
                        :placeholder="searchPlaceholder"
                        :style="inputStyle"
                        :value="search"
                        :disabled="disabled || loading || optionsLoading"
                        :tabindex="tabindex"
                        class="multiselect__input"
                        @input="updateSearch($event.target.value)"
                        @blur.prevent="deactivate()"
                    >
                    <transition name="fade">
                        <button
                            v-show="search"
                            type="button"
                            class="btn btn-default text-muted multiselect__search-inside-clear-btn"
                            @focus.stop="activate()"
                            @mousedown.prevent
                            @click="clearSearch"
                        >
                            <feather type="x" size="24" />
                        </button>
                    </transition>
                </div>
                <div
                    ref="list"
                    class="multiselect__content-wrapper"
                    tabindex="-1"
                    :style="{ maxHeight: optimizedHeight + 'px' }"
                    @focus="activate"
                    @mousedown.prevent
                >
                    <ul class="multiselect__content" :style="contentStyle">
                        <slot name="beforeList"></slot>
                        <li v-if="multiple && max === internalValue.length">
                            <span class="multiselect__option">
                                <slot name="maxElements">Maximum of {{ max }} options selected. First remove a selected
                                    option to select another.</slot>
                            </span>
                        </li>
                        <template v-if="!max || internalValue.length < max">
                            <template v-for="(partOptions, partOptionsIndex) in [filteredOptions, selectedOptionsShown]">
                                <li
                                    v-for="(option, index) in partOptions"
                                    :key="getItemKey(option)"
                                    class="multiselect__element"
                                    :class="{ 'multiselect__element-selected': partOptionsIndex === 1, 'multiselect__element-child': option.$isChild, 'multiselect__element-parent': option.$isLabel }"
                                >
                                    <span
                                        v-if="!(option && (option.$isLabel || isOptionDisabled(option)))"
                                        :class="{
                                            ...optionHighlight(index, option),
                                            'multiselect__option__checkbox': showCheckboxes,
                                            'multiselect__option-selected': partOptionsIndex === 1,
                                            'multiselect__option--disabled': !!isOptionDisabled(option),
                                        }"
                                        :data-select="showCheckboxes || !allowEmpty ? '' : selectLabelText"
                                        :data-selected="showCheckboxes || !allowEmpty ? '' : selectedLabelText"
                                        :data-deselect="showCheckboxes || !allowEmpty ? '' : deselectLabelText"
                                        class="multiselect__option"
                                        @click.stop="select(option)"
                                        @mouseenter.self="pointerSet(index)"
                                    >
                                        <slot name="option" :option="option" :search="search">
                                            <div v-if="showCheckboxes" class="multiselect__checkbox-wrapper">
                                                <div class="custom-control custom-checkbox d-flex align-items-center flex-grow-1">
                                                    <input type="checkbox" class="custom-control-input" :checked="isSelected(option)">
                                                    <div class="custom-control-label">
                                                        <slot name="optionLabel" :option="option" :search="search">
                                                            <span :title="getOptionLabel(option)">{{ getOptionLabel(option) }}</span>
                                                        </slot>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    class="btn btn-outline-primary btn-select-only"
                                                    @click.stop="selectOnly(option)"
                                                >
                                                    {{ $t('only') }}
                                                </button>
                                            </div>
                                            <span v-else>{{ getOptionLabel(option) }}</span>
                                        </slot>
                                    </span>
                                    <span
                                        v-if="option && (option.$isLabel || isOptionDisabled(option))"
                                        :data-select="showCheckboxes ? '' : groupSelect && selectGroupLabelText"
                                        :data-deselect="showCheckboxes ? '' : groupSelect && deselectGroupLabelText"
                                        :class="{
                                            ...groupHighlight(index, option),
                                            'multiselect__option__checkbox': showCheckboxes,
                                            'multiselect__option--disabled': !!isOptionDisabled(option),
                                        }"
                                        class="multiselect__option"
                                        @mouseenter.self="groupSelect && pointerSet(index)"
                                        @mousedown.prevent="selectGroup(option)"
                                    >
                                        <slot name="option" :option="option" :search="search">
                                            <div v-if="showCheckboxes" class="multiselect__checkbox-wrapper">
                                                <div class="custom-control custom-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        class="custom-control-input"
                                                        :checked="isGroupSelected(option)"
                                                        :indeterminate.prop="isGroupIndeterminate(option)"
                                                    >
                                                    <div class="custom-control-label">
                                                        <span :title="getOptionLabel(option)">{{ getOptionLabel(option) }}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    class="btn btn-light btn-select-only"
                                                    @click.stop="selectGroupOnly(option)"
                                                >
                                                    {{ $t('only') }}
                                                </button>
                                            </div>
                                            <span v-else>{{ getOptionLabel(option) }}</span>
                                        </slot>
                                    </span>
                                </li>
                                <template v-if="partOptionsIndex === 0">
                                    <li
                                        v-show="shouldShowNoOptions"
                                        :key="`noOptions-${partOptionsIndex}`"
                                    >
                                        <span class="multiselect__option multiselect__option--no-option">
                                            <slot name="noOptions">{{ noOptionsText }}</slot>
                                        </span>
                                    </li>
                                    <li
                                        v-show="shouldShowNoResults"
                                        :key="`noResult-${partOptionsIndex}`"
                                    >
                                        <span class="multiselect__option multiselect__option--no-result">
                                            <slot name="noResult" :search="search">{{ searchNotFoundText }}</slot>
                                        </span>
                                    </li>
                                    <li
                                        v-show="shouldShowMinCharWarning"
                                        :key="`minChar-${partOptionsIndex}`"
                                    >
                                        <span class="multiselect__option multiselect__option--min-char">
                                            <slot name="minChar" :search="search">
                                                <span v-text="minCharToSearchText(minCharToSearch)"></span>
                                            </slot>
                                        </span>
                                    </li>
                                    <li
                                        v-show="shouldShowDebounceLoading"
                                        :key="`debounceOptionsLoading-${partOptionsIndex}`"
                                    >
                                        <span class="multiselect__option multiselect__option--debounce-options-loading">
                                            <slot name="debounceOptionsLoading">
                                                {{ optionsLoadingText }}
                                                <div class="multiselect__spinner"></div>
                                            </slot>
                                        </span>
                                    </li>
                                    <li
                                        v-if="selectedOptionsShown.length"
                                        :key="`separator-${partOptionsIndex}`"
                                        class="multiselect__element multiselect__element--separator"
                                    >
                                        <div
                                            v-if="showCheckboxes"
                                            class="multiselect__select-all multiselect__select-all-selected rounded-0"
                                            @focus.stop="activate()"
                                            @mousedown.prevent
                                            @click="toggleAllSelected()"
                                        >
                                            <div>
                                                <div class="custom-control custom-checkbox">
                                                    <input
                                                        :checked="checkedAllSelected"
                                                        :indeterminate.prop="indeterminateAllSelected"
                                                        :disabled="!selectedOptionsShown.length"
                                                        type="checkbox"
                                                        class="custom-control-input"
                                                    >
                                                    <div class="custom-control-label">
                                                        <slot name="selectAllSelectedHeader" :values="visibleValues">
                                                            {{ previouslySelectedOptionsLabel }} ({{ selectedOptionsCount }})
                                                        </slot>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </template>
                            </template>
                        </template>
                        <slot name="afterList"></slot>
                    </ul>
                </div>
            </div>
        </transition>
    </div>
</template>



<script>
    /* eslint-disable no-nested-ternary */

    import Multiselect from 'vue-multiselect';
    import isEqual from 'lodash/isEqual';
    import sortBy from 'lodash/sortBy';
    import uniqBy from 'lodash/uniqBy';
    import debounce from 'lodash/debounce';
    import cloneDeep from 'lodash/cloneDeep';
    import { i18n } from '@/i18n/i18n';

    const flow =
        (...fns) =>
        x =>
            fns.reduce((v, f) => f(v), x);

    /**
     * Sprawdza, czy ciąg znaków zawiera zapytanie.
     * @param {String} string - Ciąg znaków do sprawdzenia.
     * @param {String} query - Zapytanie.
     * @returns {Boolean} - True, jeśli zawiera zapytanie.
     */
    function includes(string, query) {
        const str = string ?? '';
        const text = str.toString().toLowerCase();
        return text.includes(query.trim());
    }

    /**
     * Filtruje opcje na podstawie wyszukiwania.
     * @param {Array} options - Lista opcji.
     * @param {String} search - Wyszukiwana fraza.
     * @param {String} label - Klucz etykiety.
     * @param {Function} customLabel - Funkcja zwracająca etykietę.
     * @returns {Array} - Przefiltrowane opcje.
     */
    function filterOptions(options, search, label, customLabel) {
        return options.filter(option => includes(customLabel(option, label), search));
    }

    function filterGroups(search, label, values, groupLabel, customLabel) {
        return groups =>
            groups.map(group => {
                /* istanbul ignore else */
                if (!group[values]) {
                    console.warn('Options passed to vue-multiselect do not contain groups, despite the config.');
                    return [];
                }
                const groupOptions = filterOptions(group[values], search, label, customLabel);

                return groupOptions.length
                    ? {
                          [groupLabel]: group[groupLabel],
                          [values]: groupOptions,
                      }
                    : [];
            });
    }

    /**
     * Spłaszcza zagnieżdżone opcje.
     * @param {String} values - Klucz wartości.
     * @param {String} label - Klucz etykiety.
     * @returns {Function} - Funkcja przetwarzająca opcje.
     */
    function flattenOptions(values, label) {
        return options =>
            options.reduce((prev, curr) => {
                if (curr[values] && curr[values].length) {
                    prev.push({
                        $groupLabel: curr[label],
                        $isLabel: true,
                    });
                    return prev.concat(
                        curr[values].map(option => {
                            option.$isChild = true; // eslint-disable-line no-param-reassign
                            return option;
                        })
                    );
                }
                return prev;
            }, []);
    }

    /**
     * Sprawdza, czy wartość jest pusta.
     * @param {any} opt - Wartość do sprawdzenia.
     * @returns {Boolean} - True, jeśli wartość jest pusta.
     */
    function isEmpty(opt) {
        if (opt === false) return false;
        if (opt === 0) return false;
        if (Array.isArray(opt) && opt.length === 0) return true;
        return !opt;
    }

    export default {
        name: 'Multiselect',
        extends: Multiselect,
        inheritAttrs: false,
        props: {
            options: {
                type: Array,
                default: () => [],
            },
            showCheckboxes: {
                type: Boolean,
                default: false,
            },
            trackBy: {
                type: String,
                default: 'value',
            },
            trackByLabel: {
                type: String,
                default: 'value',
            },
            label: {
                type: String,
                default: 'text',
            },
            placeholder: {
                type: String,
                default: () => i18n.t('selectOption'),
            },
            searchPlaceholder: {
                type: String,
                default: () => i18n.t('selectField.searchPlaceholder'),
            },
            selectLabel: {
                type: String,
                default: '',
            },
            selectGroupLabel: {
                type: String,
                default: () => i18n.t('selectField.selectGroupLabel'),
            },
            selectedLabel: {
                type: String,
                default: () => i18n.t('selectField.selectedLabel'),
            },
            deselectLabel: {
                type: String,
                default: () => i18n.t('selectField.deselectLabel'),
            },
            deselectGroupLabel: {
                type: String,
                default: () => i18n.t('selectField.deselectGroupLabel'),
            },
            limitText: {
                type: Function,
                default: count => i18n.t('selectField.limitText', { count }),
            },
            searchNotFoundText: {
                type: String,
                default: () => i18n.t('selectField.searchNotFoundText'),
            },
            noOptionsText: {
                type: String,
                default: () => i18n.t('selectField.noOptionsText'),
            },
            previouslySelectedOptionsLabel: {
                type: String,
                default: () => i18n.t('selectField.previouslySelectedOptions'),
            },
            selectedOptionsLabel: {
                type: String,
                default: () => i18n.t('selectField.selectedOptions'),
            },
            showSelected: {
                type: Boolean,
                default: false,
            },
            asynchronous: {
                type: Boolean,
                default: false,
            },
            fetchOptionsFunction: {
                type: Function,
                default: null,
            },
            showSelectedOnTop: {
                type: Boolean,
                default: false,
            },
            parseOptionsFunction: {
                type: Function,
                default: null,
            },
            debounceDelay: {
                type: Number,
                default: 750,
            },
            minCharToSearch: {
                type: Number,
                default: 3,
                validator: value => value > 0,
            },
            minCharToSearchText: {
                type: Function,
                default: min => i18n.t('selectField.minCharToSearchText', { min }),
            },
            preselectAll: {
                type: Boolean,
                default: false,
            },
            state: {
                type: Boolean,
                default: null,
                validator: value => [null, false, true].includes(value),
            },
            optionsLoadingText: {
                type: String,
                default: () => i18n.t('selectField.optionsLoadingText'),
            },
            initialValue: undefined,
            customLabel: {
                type: Function,
                default(option, label) {
                    if (isEmpty(option)) return '';
                    if (typeof option === 'number' || typeof option === 'string') return option;
                    return label && option[label] ? option[label] : '';
                },
            },
            disabledOptions: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                modelValue: this.multiple ? [] : null,
                emittedInputData: null,
                debouncedFetchOptions: null,
                pendingFetchOptionsPromises: [],
                isDebouncedOptionsLoading: false,
                loadedOptions: [],
                loadingOptionsComplete: true,
                internalSelectedOptions: [],
                isTyping: false,
                optionsLoading: false,
                valueOnOpen: null,
            };
        },
        computed: {
            attrs() {
                return {
                    ...this.$attrs,
                    id: this.id ? `multiselect-${this.id}` : undefined,
                };
            },
            hasFetchOptionsFunction() {
                return typeof this.fetchOptionsFunction === 'function';
            },
            internalValue() {
                if (Array.isArray(this.modelValue)) {
                    return this.modelValue;
                }
                if (this.modelValue != null) {
                    return [this.modelValue];
                }
                return [];
            },
            checkedAll() {
                const enabledOptions = this.filteredOptionsWithoutLabels.filter(option => !this.isOptionDisabled(option));
                return enabledOptions.length > 0 && enabledOptions.every(option => this.isSelected(option));
            },
            indeterminateAll() {
                const enabledOptions = this.filteredOptionsWithoutLabels.filter(option => !this.isOptionDisabled(option));
                if (!enabledOptions.length) return false;
                const anySelected = enabledOptions.some(option => this.isSelected(option));
                const allSelected = enabledOptions.every(option => this.isSelected(option));
                return anySelected && !allSelected;
            },
            checkedAllFiltered() {
                const enabledOptions = this.filteredOptionsWithoutLabels.filter(option => !this.isOptionDisabled(option));
                return enabledOptions.length > 0 && enabledOptions.every(option => this.isSelected(option));
            },
            checkedAllSelected() {
                const enabledSelectedOptions = this.selectedOptionsShown.filter(option => !this.isOptionDisabled(option));
                return enabledSelectedOptions.length > 0 && enabledSelectedOptions.every(option => this.isSelected(option));
            },
            indeterminateAllFiltered() {
                const enabledOptions = this.filteredOptionsWithoutLabels.filter(option => !this.isOptionDisabled(option));
                if (!enabledOptions.length) return false;
                const anySelected = enabledOptions.some(option => this.isSelected(option));
                const allSelected = enabledOptions.every(option => this.isSelected(option));
                return anySelected && !allSelected;
            },
            indeterminateAllSelected() {
                const enabledSelectedOptions = this.selectedOptionsShown.filter(option => !this.isOptionDisabled(option));
                if (!enabledSelectedOptions.length) return false;
                const anySelected = enabledSelectedOptions.some(option => this.isSelected(option));
                const allSelected = enabledSelectedOptions.every(option => this.isSelected(option));
                return anySelected && !allSelected;
            },
            contentStyle() {
                return { display: 'block' };
            },
            optionsValues() {
                return this.filteredOptionsWithoutLabels.map(option => this.getItemValue(option));
            },
            selectedOptions() {
                const optionsValues = this.optionsValues;
                const value = Array.isArray(this.value) ? this.value : [];
                const normalize = item => (item && typeof item === 'object' ? item : { [this.trackBy]: item });
                const merged = [...this.internalSelectedOptions, ...this.internalValue, ...(Array.isArray(value) ? value : value ? [value] : [])];
                const uniqueValues = uniqBy(merged.map(normalize), this.trackBy);
                const filteredValues = uniqueValues.filter(item => !optionsValues.includes(this.getItemValue(item)));
                return sortBy(filteredValues, this.label);
            },
            selectedOptionsShown() {
                return this.showSelected ? this.selectedOptions : [];
            },
            selectedOptionsCount() {
                return this.selectedOptionsShown.length;
            },
            optionsFilteredPlusSelected() {
                return [...this.filteredOptionsWithoutLabels, ...this.selectedOptionsShown];
            },
            filteredOptions() {
                let options = [...this.options];

                if (this.asynchronous && this.loadedOptions.length) {
                    options = this.loadedOptions;
                }

                if (!this.asynchronous && this.hasFetchOptionsFunction) {
                    options = this.loadedOptions;
                }

                const search = this.search || '';
                const normalizedSearch = search.toLowerCase().trim();

                if (this.internalSearch) {
                    options = this.groupValues ? this.filterAndFlat(options, normalizedSearch, this.label) : filterOptions(options, normalizedSearch, this.label, this.customLabel);
                } else {
                    options = this.groupValues ? flattenOptions(this.groupValues, this.groupLabel)(options) : options;
                }

                if (this.taggable && normalizedSearch.length && !this.isExistingOption(normalizedSearch)) {
                    const tagOption = { isTag: true, label: search };
                    if (this.tagPosition === 'bottom') {
                        options.push(tagOption);
                    } else {
                        options.unshift(tagOption);
                    }
                }

                if (this.showSelectedOnTop && this.valueOnOpen) {
                    options = options.sort((a, b) => {
                        const aWasSelectedOnOpen = this.valueOnOpen.some(item => this.getItemValue(item) === this.getItemValue(a));
                        const bWasSelectedOnOpen = this.valueOnOpen.some(item => this.getItemValue(item) === this.getItemValue(b));

                        // Only move items that were already selected when the dropdown opened
                        if (aWasSelectedOnOpen && !bWasSelectedOnOpen) return -1;
                        if (!aWasSelectedOnOpen && bWasSelectedOnOpen) return 1;
                        return 0;
                    });
                }

                return options;
            },
            filteredOptionsWithoutLabels() {
                return this.filteredOptions.filter(option => !option.$isLabel);
            },
            disabledIds() {
                if (!this.disabledOptions) return [];
                if (typeof this.disabledOptions[0] === 'object') {
                    return this.disabledOptions.map(item => item[this.trackBy]);
                }
                return this.disabledOptions;
            },
            shouldShowNoOptions() {
                return this.showNoOptions && this.filteredOptionsWithoutLabels.length === 0 && !this.search && !this.loading;
            },
            shouldShowNoResults() {
                return this.showNoResults && this.filteredOptionsWithoutLabels.length === 0 && this.search.length >= this.minCharToSearch && !this.loading && this.loadingOptionsComplete;
            },
            shouldShowMinCharWarning() {
                return this.asynchronous && this.search && this.search.length < this.minCharToSearch;
            },
            shouldShowDebounceLoading() {
                return this.asynchronous && this.isTyping && !this.showCheckboxes;
            },
        },
        watch: {
            value: {
                handler(newVal, oldVal) {
                    if (isEmpty(oldVal) && isEmpty(newVal)) {
                        return;
                    }
                    this.initModelValue(this.value);
                    this.internalSelectedOptions = [];
                },
                immediate: true,
            },
            initialValue: {
                handler(newVal, oldVal) {
                    if (isEmpty(oldVal) && isEmpty(newVal)) {
                        return;
                    }
                    this.handleInitialValueChange(newVal, oldVal);
                },
                immediate: true,
            },
            options: {
                handler() {
                    if (isEmpty(this.options)) {
                        return;
                    }

                    // 1) jeśli mamy value z rodzica → zsynchronizuj od razu
                    if (this.value !== undefined && (Array.isArray(this.value) ? !!this.value.length : true)) {
                        this.initModelValue(this.value);
                        return;
                    }

                    // 2) inaczej, jeśli jest initialValue → ustaw initial
                    if (this.initialValue !== undefined && (Array.isArray(this.initialValue) ? !!this.initialValue.length : true)) {
                        this.handleInitialValueChange(this.initialValue, null);
                        return;
                    }

                    // 3) fallback: preselect (jak wcześniej)
                    this.preselectOptions();
                },
                immediate: false,
            },
            isOpen() {
                if (!this.isOpen && this.showSelected) {
                    this.internalSelectedOptions = [...this.internalValue];
                    this.$nextTick(() => {
                        if (this.$refs.list) {
                            this.$refs.list.scrollTop = 0;
                        }
                    });
                }
            },
            search() {
                this.handleSearchChange();
            },
            pendingFetchOptionsPromises(promises) {
                this.handlePendingFetchOptionsPromises(promises);
            },
            isDebouncedOptionsLoading(loading) {
                this.$emit('loading', loading);
                if (!loading) this.loadingOptionsComplete = true;
            },
            loadingOptionsComplete(loading) {
                if (loading) this.isTyping = false;
            },
        },
        methods: {
            mapToKeys(inputValue) {
                const toArray = value => (Array.isArray(value) ? value : value === null || value === undefined ? [] : [value]);

                return toArray(inputValue)
                    .filter(item => {
                        if (item && typeof item === 'object') {
                            if (item.$isLabel) return false;
                            if (item.isTag) return false;
                        }
                        return true;
                    })
                    .map(item => (this.trackBy && typeof item === 'object' ? item[this.trackBy] : item))
                    .filter(key => key !== undefined && key !== null);
            },
            /**
             * Filters and then flattens the options list
             * @param  {Array}
             * @returns {Array} returns a filtered and flat options list
             */
            filterAndFlat(options, search, label) {
                return flow(filterGroups(search, label, this.groupValues, this.groupLabel, this.customLabel), flattenOptions(this.groupValues, this.groupLabel))(options);
            },
            /**
             * Zwraca unikalną wartość jako klucz
             */
            getItemKey(option) {
                if (typeof option === 'object') return option[this.trackBy] || option.id || option.value;
                return option;
            },
            /**
             * Obsługuje zmianę wartości wyszukiwania.
             */
            handleSearchChange() {
                if (this.asynchronous) {
                    this.loadedOptions = [];
                    this.loadingOptionsComplete = false;
                    this.isTyping = false;
                    if (this.search.length >= this.minCharToSearch) {
                        this.isTyping = true;
                        this.debouncedFetchOptions?.();
                    } else {
                        this.pendingFetchOptionsPromises = [];
                        this.isDebouncedOptionsLoading = false;
                    }
                }
                if (this.showSelected) {
                    this.internalSelectedOptions = uniqBy([...this.internalSelectedOptions, ...this.internalValue], this.trackBy);
                    this.$nextTick(() => {
                        if (this.$refs.list) {
                            this.$refs.list.scrollTop = 0;
                        }
                    });
                }
            },
            /**
             * Obsługuje zmiany w liście oczekujących obietnic pobierania opcji.
             * @param {Array} promises - Lista obietnic.
             */
            handlePendingFetchOptionsPromises(promises) {
                const promisesLength = promises.length;
                if (promisesLength) {
                    this.isDebouncedOptionsLoading = true;
                    Promise.all(promises)
                        .then(values => {
                            if (promisesLength === this.pendingFetchOptionsPromises.length && this.search) {
                                const lastResponse = values[values.length - 1];
                                this.loadedOptions = this.parseOptionsFunction ? this.parseOptionsFunction(lastResponse) : lastResponse || [];
                            }
                        })
                        .catch(error => {
                            if (promisesLength === this.pendingFetchOptionsPromises.length && this.search) {
                                this.loadedOptions = [];
                            }
                        })
                        .finally(() => {
                            if (promisesLength === this.pendingFetchOptionsPromises.length && this.search) {
                                this.pendingFetchOptionsPromises = [];
                                this.isDebouncedOptionsLoading = false;
                            }
                        });
                }
            },
            /**
             * Obsługuje zmianę wartości początkowej.
             * @param {any} newVal - Nowa wartość.
             * @param {any} oldVal - Stara wartość.
             */
            handleInitialValueChange(newVal, oldVal) {
                // 0) jeśli nie ma opcji do pracy – wyjdź
                if (!(this.hasFetchOptionsFunction || (this.options && this.options.length))) return;

                // 1) allow-empty === false + puste initialValue -> wymuś wybór
                const isEmptyInitial = isEmpty(newVal) || (Array.isArray(newVal) && newVal.length === 0);
                if (this.allowEmpty === false && isEmptyInitial) {
                    // bierz tylko realne opcje (bez labeli grup) i nie-disabled
                    const enabledOptions = this.filteredOptionsWithoutLabels.filter(o => !this.isOptionDisabled(o));
                    if (enabledOptions.length) {
                        if (this.preselectAll && (this.multiple || this.showCheckboxes)) {
                            this.modelValue = [...enabledOptions];
                        } else {
                            this.modelValue = this.multiple ? [enabledOptions[0]] : enabledOptions[0];
                        }
                        this.emitInput();
                    }
                    return;
                }

                // 2) short-circuit: jeśli newVal == current (z normalizacją pod trackBy)
                const normalizeToKeys = value => {
                    if (Array.isArray(value)) {
                        return sortBy(value.map(val => (this.trackBy && val && typeof val === 'object' ? val[this.trackBy] : val)));
                    }
                    return this.trackBy && value && typeof value === 'object' ? value[this.trackBy] : value;
                };

                const currentModelArray = !Array.isArray(this.modelValue) && this.multiple ? [this.modelValue] : this.modelValue || [];

                const modelValueKeys = this.trackBy ? (Array.isArray(currentModelArray) ? currentModelArray.map(i => (i && typeof i === 'object' ? i[this.trackBy] : i)) : currentModelArray) : currentModelArray;

                const newInitialKeys = normalizeToKeys(newVal);

                if (JSON.stringify(modelValueKeys) === JSON.stringify(newInitialKeys)) {
                    return; // nic się faktycznie nie zmienia
                }

                // 3) standardowa inicjalizacja
                if (!this.multiple) {
                    if (this.modelValue) {
                        if (this.trackBy) {
                            if (this.initialValue === this.modelValue[this.trackBy]) return;
                        } else if (this.initialValue === this.modelValue) return;
                    }
                }

                const changed = JSON.stringify(newVal) !== JSON.stringify(oldVal) && JSON.stringify(newVal) !== JSON.stringify(this.modelValue);

                if (!changed) return;

                const processedInitialValue = this.processInitialValue(this.initialValue);

                if (this.trackBy) {
                    this.modelValue = this.multiple ? processedInitialValue : processedInitialValue[0] || null;
                } else if (this.multiple) {
                    this.modelValue = Array.isArray(this.initialValue) ? [...this.initialValue] : [this.initialValue];
                } else {
                    this.modelValue = this.initialValue === null || this.initialValue === undefined ? null : this.initialValue;
                }
            },
            /**
             * Przetwarza wartość początkową na odpowiedni format.
             * @param {any} initialValue - Wartość początkowa.
             * @returns {Array} - Przetworzona wartość początkowa.
             */
            processInitialValue(initialValue) {
                const fnMap = item => {
                    if (item && typeof item === 'object') return item;
                    return {
                        [this.trackBy]: item,
                        [this.label]: this.getOptionLabel(item),
                        isInitial: true,
                    };
                };
                if (Array.isArray(initialValue)) {
                    return initialValue.map(fnMap);
                }
                return [fnMap(initialValue)];
            },
            /**
             * Zwraca etykietę dla opcji.
             * @param {Object} option - Opcja.
             * @returns {String} - Etykieta opcji.
             */
            getOptionLabel(option) {
                if (isEmpty(option)) return '';
                if (option.isTag) return option.label;
                if (option.$isLabel) return option.$groupLabel;
                if (option.isInitial && option[this.trackBy] === option[this.label] && this.trackByLabel) {
                    return `${this.trackByLabel}: ${option[this.label]}`;
                }
                const label = this.customLabel(option, this.label);
                if (isEmpty(label)) return '';
                return label;
            },
            /**
             * Aktualizuje etykietę w wartości początkowej, jeśli jest to konieczne.
             * @param {Object} option - Opcja.
             * @param {Boolean} isSelected - Czy opcja jest zaznaczona.
             * @param {Number} index - Indeks opcji.
             */
            updateLabelInInitialValue(option, isSelected, index) {
                const modelValueItem = this.multiple ? this.modelValue?.[index] : this.modelValue;
                if (this.initialValue && modelValueItem?.isInitial && isSelected && this.trackBy && !option.isInitial && modelValueItem?.[this.trackBy] === option[this.trackBy] && modelValueItem?.[this.label] !== option[this.label]) {
                    Object.keys(option).forEach(key => {
                        this.$set(modelValueItem, [this.label], this.getOptionLabel(option));
                        this.$set(modelValueItem, key, option[key]);
                    });
                    this.$set(modelValueItem, 'isInitial', false);
                }
            },
            /**
             * Sprawdza, czy opcja jest zaznaczona.
             * @param {Object} option - Opcja.
             * @returns {Boolean} - Czy opcja jest zaznaczona.
             */
            isSelected(option) {
                const optionValue = this.getItemValue(option);
                const isSelected = this.valueKeys.includes(optionValue);
                if (isSelected) {
                    const index = this.valueKeys.indexOf(optionValue);
                    this.updateLabelInInitialValue(option, isSelected, index);
                }
                return isSelected;
            },
            /**
             * Wybiera opcję.
             * @param {Object} option - Opcja.
             * @param {String} key - Klawisz.
             */
            select(option, key) {
                if (option.$isLabel && this.groupSelect) {
                    this.selectGroup(option);
                    return;
                }
                if (this.isSelectionBlocked(option, key)) return;

                if (key === 'Tab' && !this.pointerDirty) return;

                if (option.isTag) {
                    this.handleTagSelection(option);
                } else {
                    this.handleOptionSelection(option);
                }

                if (this.closeOnSelect) this.deactivate();
            },
            /**
             * Sprawdza, czy wybór opcji jest zablokowany.
             * @param {Object} option - Opcja.
             * @param {String} key - Klawisz.
             * @returns {Boolean} - Czy wybór jest zablokowany.
             */
            isSelectionBlocked(option, key) {
                return this.blockKeys.includes(key) || this.disabled || this.isOptionDisabled(option) || option.$isLabel || (this.max && this.multiple && this.internalValue.length === this.max);
            },
            /**
             * Add the given group options to the list of selected options
             * If all group optiona are already selected -> remove it from the results.
             *
             * @param  {Object||String||Integer} group to select/deselect
             */
            selectGroup(selectedGroup) {
                const group = this.options.find(option => option[this.groupLabel] === selectedGroup.$groupLabel);

                if (!group) return;

                if (this.wholeGroupSelected(group)) {
                    this.$emit('remove', group[this.groupValues], this.id);

                    const groupValues = this.trackBy ? group[this.groupValues].map(val => val[this.trackBy]) : group[this.groupValues];
                    const newValue = this.internalValue.filter(option => groupValues.indexOf(this.trackBy ? option[this.trackBy] : option) === -1);

                    this.$emit('input', newValue, this.id);
                } else {
                    const optionsToAdd = group[this.groupValues].filter(option => !(this.isOptionDisabled(option) || this.isSelected(option)));

                    // if max is defined then just select options respecting max
                    if (this.max) {
                        optionsToAdd.splice(this.max - this.internalValue.length);
                    }

                    this.$emit('select', optionsToAdd, this.id);
                    this.$emit('input', this.internalValue.concat(optionsToAdd), this.id);
                }

                if (this.closeOnSelect) this.deactivate();
            },
            /**
             * Obsługuje wybór tagu.
             * @param {Object} option - Opcja.
             */
            handleTagSelection(option) {
                this.$emit('tag', option.label, this.id);
                this.search = '';
                if (this.closeOnSelect && !this.multiple) this.deactivate();
            },
            /**
             * Obsługuje wybór opcji.
             * @param {Object} option - Opcja.
             */
            handleOptionSelection(option) {
                const isSelected = this.isSelected(option);

                if (isSelected) {
                    this.removeElement(option);
                    return;
                }

                this.$emit('select', option, this.id);

                const val = this.multiple ? [...this.internalValue, option] : option;
                if (this.showCheckboxes && this.asynchronous) {
                    this.modelValue = val;
                    this.$emit('input', this.modelValue, this.id);
                } else {
                    this.$emit('input', val, this.id);
                }

                if (this.clearOnSelect) this.search = '';
            },
            /**
             * Usuwa element z listy wybranych.
             * @param {Object} option - Opcja.
             * @param {Boolean} shouldClose - Czy zamknąć po usunięciu.
             */
            removeElement(option, shouldClose = true) {
                if (this.disabled) return;
                if (this.isOptionDisabled(option)) return;

                const index = typeof option === 'object' ? this.valueKeys.indexOf(option[this.trackBy]) : this.valueKeys.indexOf(option);

                this.$emit('remove', option, this.id);
                if (this.multiple) {
                    const newValue = this.internalValue.slice(0, index).concat(this.internalValue.slice(index + 1));
                    this.$emit('input', newValue, this.id);
                } else {
                    this.$emit('input', null, this.id);
                }

                if (this.closeOnSelect && shouldClose) this.deactivate();
            },
            onSearchFocus() {
                this.activate();
                if (this.showCheckboxes) this.$refs.searchInside.focus();
            },
            onSearchBlur() {
                if (!this.showCheckboxes) this.deactivate();
            },
            toggleAll() {
                // Jeśli wszystkie są zaznaczone, odznaczamy wszystkie
                if (this.checkedAll) {
                    this.deselectAll();
                } else {
                    // Zaznaczamy wszystkie nowe opcje, nie zmieniając już zaznaczonych
                    this.selectAll();
                }
            },
            selectAll() {
                // filtrujemy tylko opcje nie‐grupowe
                const enabledOptions = this.filteredOptionsWithoutLabels.filter(option => !this.isOptionDisabled(option));

                const newOptions = enabledOptions.filter(option => !this.isSelected(option));
                this.$emit('input', [...this.internalValue, ...newOptions], this.id);
            },
            deselectAll() {
                // również pomijamy nagłówki grup
                const enabledOptions = this.filteredOptionsWithoutLabels.filter(option => !this.isOptionDisabled(option));

                const enabledValues = enabledOptions.map(op => (this.trackBy && typeof op === 'object' ? op[this.trackBy] : op));

                const remainingOptions = this.internalValue.filter(item => {
                    const key = this.trackBy && typeof item === 'object' ? item[this.trackBy] : item;
                    return !enabledValues.includes(key);
                });

                this.$emit('input', remainingOptions, this.id);
            },
            /**
             * Przełącza zaznaczenie wszystkich filtrowanych opcji.
             */
            toggleAllFiltered() {
                const enabledOptions = this.filteredOptionsWithoutLabels.filter(option => !this.isOptionDisabled(option));
                if (!enabledOptions.length) return;

                const allSelected = enabledOptions.every(option => this.isSelected(option));
                if (allSelected) {
                    const keysToRemove = enabledOptions.map(option => this.getItemValue(option));
                    const remainingSelected = this.internalValue.filter(selectedOption => !keysToRemove.includes(this.getItemValue(selectedOption)));
                    this.$emit('input', remainingSelected, this.id);
                } else {
                    const optionsToAdd = enabledOptions.filter(option => !this.isSelected(option));
                    this.$emit('input', [...this.internalValue, ...optionsToAdd], this.id);
                }
            },
            /**
             * Przełącza zaznaczenie wszystkich wybranych opcji.
             */
            toggleAllSelected() {
                const enabledSelectedOptions = this.selectedOptionsShown.filter(option => !this.isOptionDisabled(option));
                if (this.checkedAllSelected) {
                    const remainingOptions = this.internalValue.filter(item => {
                        if (this.trackBy && typeof item === 'object') {
                            return !enabledSelectedOptions.map(op => op[this.trackBy]).includes(item[this.trackBy]);
                        }
                        return !enabledSelectedOptions.includes(item);
                    });
                    this.$emit('input', remainingOptions, this.id);
                } else {
                    const newOptions = enabledSelectedOptions.filter(option => !this.isSelected(option));
                    this.$emit('input', [...this.internalValue, ...newOptions], this.id);
                }
            },
            /**
             * Wybiera tylko jedną opcję.
             * @param {Object} option - Opcja.
             */
            selectOnly(option) {
                this.$emit('input', [option], this.id);
            },
            /**
             * Wybiera tylko jedną grupę.
             * @param {Object} option - Opcja.
             */
            selectGroupOnly(option) {
                const group = [...this.loadedOptions, ...this.options].find(item => item[this.groupLabel] === option.$groupLabel);
                this.$emit('input', group[this.groupValues], this.id);
            },
            clearSearch() {
                this.search = '';
            },
            emitTrackByEvent(value, eventName) {
                if (this.trackBy) {
                    if (value === null) {
                        this.$emit(eventName, value);
                    } else if (typeof value === 'object') {
                        if (Array.isArray(value)) {
                            this.$emit(
                                eventName,
                                value.map(item => item[this.trackBy])
                            );
                        } else {
                            this.$emit(eventName, value[this.trackBy]);
                        }
                    }
                }
            },
            onInputEmitted(value, id) {
                let sorted = value;
                if (this.multiple && Array.isArray(value)) {
                    sorted = sortBy(value, item => (typeof item === 'object' ? item[this.trackBy] : item));
                }
                this.modelValue = sorted;
                this.emitTrackByEvent(sorted, 'inputTrackBy');
            },
            onOpenEmitted() {
                this.valueOnOpen = cloneDeep(this.modelValue);
            },
            /**
             * Zwraca płaską listę wszystkich aktywnych (niezablokowanych) opcji — także dla grup.
             */
            getAllEnabledFlatOptions() {
                const source = (this.hasFetchOptionsFunction ? this.loadedOptions : this.options) || [];
                if (this.groupValues && Array.isArray(source)) {
                    const flat = [];
                    source.forEach(grp => {
                        const arr = grp && Array.isArray(grp[this.groupValues]) ? grp[this.groupValues] : [];
                        arr.forEach(opt => {
                            if (!this.isOptionDisabled(opt)) flat.push(opt);
                        });
                    });
                    return flat;
                }
                return source.filter(item => !this.isOptionDisabled(item));
            },
            /**
             * Przy zamknięciu — jeśli allow-empty="false" i wybór pusty → przywracaj:
             * 1) preselectFirst → pierwszy element
             * 2) preselectAll → wszystkie (dla single: pierwszy)
             * 3) initialValue → jeśli uda się zmapować
             * 4) wszystkie (dla single: pierwszy)
             * Zwraca true, jeśli dokonano korekty.
             */
            ensureNonEmptyOnClose() {
                if (this.allowEmpty !== false) return false;

                const len = Array.isArray(this.modelValue) ? this.modelValue.length : this.modelValue ? 1 : 0;
                if (len) return false;

                const enabled = this.getAllEnabledFlatOptions();
                if (!enabled.length) return false;

                const asFirst = () => (this.multiple ? [enabled[0]] : enabled[0]);
                const asAll = () => (this.multiple || this.showCheckboxes ? [...enabled] : enabled[0]);

                // 1) preselectFirst
                if (this.preselectFirst) {
                    this.modelValue = asFirst();
                    return true;
                }

                // 2) preselectAll
                if (this.preselectAll) {
                    this.modelValue = asAll();
                    return true;
                }

                // 3) initialValue
                if (this.initialValue !== undefined && (Array.isArray(this.initialValue) ? this.initialValue.length : this.initialValue !== null)) {
                    const prev = this.modelValue;
                    this.modelValue = this.multiple ? [] : null;
                    this.initModelValue(this.initialValue);
                    const ok = Array.isArray(this.modelValue) ? this.modelValue.length > 0 : !!this.modelValue;
                    if (ok) return true;
                    this.modelValue = prev;
                }

                // 4) wszystkie
                this.modelValue = asAll();
                return true;
            },
            onCloseEmitted(value, id) {
                // DODANE: auto-przywracanie, jeśli allow-empty="false" i nic nie zaznaczono
                this.ensureNonEmptyOnClose();

                const sortKey = typeof this.value?.[0] === 'object' ? this.label : null;
                const changed = !isEqual(this.mapToKeys(sortBy(this.modelValue, sortKey)), this.mapToKeys(sortBy(this.valueOnOpen, sortKey)));
                if (changed) {
                    this.$emit('input', this.modelValue, id);
                    this.emitChange();
                }
                this.valueOnOpen = null;
            },
            initAsynchronous() {
                if (this.asynchronous && this.hasFetchOptionsFunction) {
                    this.debouncedFetchOptions = debounce(this.debounceHandler, this.debounceDelay);
                }
                if (!this.asynchronous && this.hasFetchOptionsFunction) {
                    this.fetchOptions();
                }
            },
            debounceHandler() {
                if (this.search.length >= this.minCharToSearch) {
                    const promise = this.fetchOptionsFunction(this.getOptionsFunctionParams());
                    this.pendingFetchOptionsPromises.push(promise);
                }
            },
            getItemValue(item) {
                if (this.trackBy && typeof item === 'object') return item[this.trackBy];
                return item;
            },
            getOptionsFunctionParams() {
                return {
                    search: this.search,
                    value: this.value,
                    modelValue: this.modelValue,
                };
            },
            /**
             * Pobiera opcje asynchronicznie.
             */
            async fetchOptions() {
                try {
                    this.optionsLoading = true;
                    this.$emit('loading', true);
                    const response = await this.fetchOptionsFunction({
                        ...this.getOptionsFunctionParams(),
                        initialValue: this.initialValue,
                    });
                    this.loadedOptions = this.parseOptionsFunction ? this.parseOptionsFunction(response) : response || [];
                } catch (error) {
                    // error
                } finally {
                    this.optionsLoading = false;
                    this.$emit('loading', false);
                }

                if (this.preselectFirst) {
                    if (this.multiple) this.modelValue = [this.loadedOptions[0]];
                    else this.modelValue = this.loadedOptions[0];
                } else if (this.preselectAll) {
                    this.modelValue = this.loadedOptions;
                } else if (this.initialValue !== undefined && (Array.isArray(this.initialValue) ? !!this.initialValue.length : true)) {
                    this.initModelValue(this.initialValue);
                } else if (this.value !== undefined && (Array.isArray(this.value) ? !!this.value.length : true)) {
                    this.initModelValue(this.value);
                } else {
                    this.initModelValue();
                }

                if ((Array.isArray(this.modelValue) && this.modelValue.length) || (!Array.isArray(this.modelValue) && this.modelValue)) {
                    this.emitInput();
                    this.emitChange();
                }
            },
            /**
             * Inicjalizuje wartość modelu.
             * @param {any} value - Wartość.
             */
            initModelValue(value) {
                // 0) Rodzic jawnie wyczyścił v-model -> wyczyść natychmiast lokalny model
                if (value === null || (Array.isArray(value) && value.length === 0)) {
                    this.modelValue = this.multiple ? [] : null;
                    return;
                }

                if (isEqual(value, this.modelValue)) {
                    return; // Nie ma potrzeby ponownej inicjalizacji
                }

                let newModelValue = null;

                const options = (() => {
                    if (!this.hasFetchOptionsFunction) return this.options || [];
                    if (this.asynchronous) {
                        const union = [...(this.loadedOptions || []), ...(this.selectedOptions || [])];
                        return this.trackBy ? uniqBy(union, this.trackBy) : union;
                    }
                    return this.loadedOptions || [];
                })();

                // >>> WAŻNE: jeżeli mamy grupy, pracuj na spłaszczonych dzieciach grup
                const flatOptions = this.groupValues && Array.isArray(options) ? options.reduce((acc, grp) => acc.concat(grp[this.groupValues] || []), []) : options;

                if (!this.multiple) {
                    const baseValue = value && typeof value === 'object' && this.trackBy ? value[this.trackBy] : value;

                    if (typeof flatOptions[0] === 'object') {
                        if (this.trackBy) {
                            newModelValue = flatOptions.find(item => item[this.trackBy] === baseValue) || null;
                        } else {
                            newModelValue = flatOptions.find(item => item === baseValue) || null;
                        }
                    } else if (this.asynchronous && this.hasFetchOptionsFunction) {
                        newModelValue = value;
                    } else {
                        newModelValue = flatOptions.find(item => item === baseValue) || null;
                    }
                } else {
                    // multiple: dopasowujemy po spłaszczonych opcjach
                    newModelValue = flatOptions.filter(option => this.searchOptionsFunction(option, value));
                }

                // Uwaga: nie czyść modelValue, jeśli nie udało się nic zmapować
                const hasAnyMatch = Array.isArray(newModelValue) ? newModelValue.length > 0 : Boolean(newModelValue);
                if (hasAnyMatch) {
                    this.modelValue = newModelValue;
                }
            },
            /**
             * Funkcja wyszukiwania opcji.
             * @param {Object} option - Opcja.
             * @param {any} value - Wartość.
             * @returns {Boolean} - Czy opcja pasuje.
             */
            searchOptionsFunction(option, value) {
                const getKey = item => (item && typeof item === 'object' ? item[this.trackBy] : item); // pozwala na prymitywy (np. id) i obiekty

                const matchOne = (opt, val) => {
                    const optKey = getKey(opt);
                    const valKey = getKey(val);
                    return isEqual(String(optKey), String(valKey));
                };

                if (this.multiple || this.showCheckboxes) {
                    const arr = Array.isArray(value) ? value : value ? [value] : [];
                    return arr.some(val => matchOne(option, val));
                }
                return matchOne(option, value);
            },
            /**
             * Sprawdza, czy opcja jest zablokowana.
             * @param {Object} option - Opcja.
             * @returns {Boolean} - Czy opcja jest zablokowana.
             */
            isOptionDisabled(option) {
                if (typeof option === 'object') return !!option.$isDisabled || this.disabledIds.includes(this.getItemValue(option));
                return false;
            },
            emitInput() {
                this.$emit('input', this.modelValue, this.id);
            },
            emitChange() {
                this.emitTrackByEvent(this.modelValue, 'changeTrackBy');
                this.$emit('change', this.modelValue, this.id);
            },
            /**
             * Sprawdza, czy wszystkie elementy danej grupy są zaznaczone.
             */
            isGroupSelected(labelOption) {
                const groupName = labelOption && labelOption.$groupLabel;
                if (!groupName) return false;

                const src = Array.isArray(this.options) ? this.options : [];
                const group = src.find(item => item && item[this.groupLabel] === groupName);
                const children = group && Array.isArray(group[this.groupValues]) ? group[this.groupValues] : [];
                if (!children.length) return false;

                const enabledChildren = children.filter(childOption => !this.isOptionDisabled(childOption));
                return enabledChildren.length > 0 && enabledChildren.every(childOption => this.isSelected(childOption));
            },
            /**
             * Sprawdza, czy część (ale nie wszystkie) elementy grupy są zaznaczone.
             */
            isGroupIndeterminate(labelOption) {
                const groupName = labelOption && labelOption.$groupLabel;
                if (!groupName) return false;

                const src = Array.isArray(this.options) ? this.options : [];
                const group = src.find(item => item && item[this.groupLabel] === groupName);
                const children = group && Array.isArray(group[this.groupValues]) ? group[this.groupValues] : [];
                if (!children.length) return false;

                const some = children.some(opt => this.isSelected(opt));
                const all = children.every(opt => this.isSelected(opt));
                return some && !all;
            },
            preselectOptions() {
                if (this.preselectFirst && this.filteredOptionsWithoutLabels.length) {
                    this.select(this.filteredOptionsWithoutLabels[0]);
                }
                if (this.preselectAll && this.options.length) {
                    this.selectAll();
                }
            },
        },
        created() {
            this.$on('input', this.onInputEmitted);
            this.$on('open', this.onOpenEmitted);
            this.$on('close', this.onCloseEmitted);
            this.preselectOptions();
            this.initAsynchronous();
        },
        beforeDestroy() {
            this.$off('input', this.onInputEmitted);
            this.$off('open', this.onOpenEmitted);
            this.$off('close', this.onCloseEmitted);
        },
    };
</script>



<style lang="scss" scoped>
    @import '../../design/index';

    .multiselect,
    .multiselect__input,
    .multiselect__single {
        font-size: $input-font-size;
    }
    .multiselect__single {
        white-space: nowrap;
        overflow: hidden;
        user-select: none;
        text-overflow: ellipsis;
        display: block;
    }

    .multiselect__placeholder {
        user-select: none;
    }

    .multiselect__option {
        width: 100%;

        &.multiselect__option {
            width: 100%;
            background: $white;
            color: $dropdown-link-color;
            padding: 0.25rem 1.25rem;
            display: flex;
            align-items: center;

            &--highlight,
            &--highlight::after {
                background: $dropdown-link-hover-bg;
                color: $dropdown-link-hover-color;
                font-weight: normal;
            }

            &--selected,
            &--selected::after {
                background: darken($dropdown-link-hover-bg, 2);
                color: darken($dropdown-link-hover-color, 2);
                font-weight: normal;
            }

            &--no-result {
                white-space: normal;
            }

            &--group {
                background: #ededed;
                color: #35495e;
            }

            &--no-option,
            &--no-result,
            &--min-char {
                cursor: default;
            }

            &-selected {
                background: $gray-200 !important;
                border-color: $gray-300 !important;
            }

            &__checkbox {
                min-height: 30px;

                &.multiselect__option--highlight,
                &.multiselect__option--selected,
                &.multiselect__option--highlight::after,
                &.multiselect__option--selected::after {
                    background: $white;
                    color: $dropdown-link-color;
                }
            }
        }
    }

    .multiselect__tags {
        position: relative;
    }

    .multiselect__tag {
        display: inline-flex;
        margin: 2px !important;
    }

    .multiselect__checkbox-wrapper {
        min-width: 100%;
        height: 100%;
        min-height: 26px;
        display: flex;
        align-items: center;
        flex-grow: 1;

        .custom-control.custom-checkbox {
            overflow: hidden;

            .custom-control-label {
                min-height: $font-size-base * $line-height-base;
                display: flex;
                align-items: center;

                &::before,
                &::after {
                    top: 50%;
                    transform: translateY(-50%);
                }

                span {
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    overflow: hidden;
                }
            }
        }

        .btn-select-only {
            padding: $btn-padding-y-sm $btn-padding-x-sm;
            font-size: $btn-font-size-sm;
            text-transform: uppercase;
            display: none;
            margin-left: auto;
            line-height: 1rem;
        }
    }

    .multiselect__content-container {
        min-width: 100%;
        position: relative;
        z-index: 50;
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;

        .custom-control-input {
            &:checked,
            &:indeterminate {
                ~ .custom-control-label::before {
                    border-color: $gray-300;
                }
            }

            &:disabled {
                cursor: not-allowed;

                ~ .custom-control-label {
                    cursor: not-allowed;
                }
            }
        }

        .multiselect__select-all {
            width: 100%;
            background: $primary;
            padding: 5px 10px;
            margin: 0;
            color: #fff;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
            cursor: pointer;

            &-selected {
                opacity: 0.8;
            }
        }

        .multiselect__search-inside {
            position: relative;
            display: flex;
            padding: 0.5rem 0.75rem;
            min-height: 40px;
            border-bottom: 1px solid #e8e8e8;
            background: #fff;
            font-size: 14px;

            input {
                flex-grow: 1;
            }

            &-icon {
                width: 26px;
                height: 24px;
                flex-shrink: 0;
                margin-right: 0.75rem;
            }

            &-clear-btn {
                width: 36px;
                height: 24px;
                flex-shrink: 0;
                padding: 0;
            }
        }

        .multiselect__content-wrapper {
            width: unset;
            min-width: 100%;

            .multiselect__content {
                min-width: 100%;
                margin: 0;
                display: flex;
                flex-direction: column;

                .multiselect__element {
                    &.multiselect__element-child {
                        .multiselect__option {
                            &.multiselect__option {
                                // padding-left: 1.5rem;
                            }
                        }
                    }
                    &.multiselect__element-parent {
                        .multiselect__option {
                            &.multiselect__option {
                                background: #ced4da;
                                padding-left: 1rem;
                            }
                        }
                    }
                }
            }
        }

        .multiselect__element {
            position: relative;
            overflow: hidden;

            &:hover {
                .btn-select-only {
                    display: block;
                }
            }

            &.multiselect__element-child {
                .custom-control-label {
                    padding-left: 2rem;
                }
            }

            &.multiselect__element-parent {
                .custom-control-label {
                    font-weight: bold;
                    font-style: italic;
                }
            }
        }

        &--checkboxes {
            width: 100%;
            position: absolute;
            top: 0;
            box-shadow: 0px 3px 15px rgba(0, 0, 0, 0.2);

            .multiselect__content-wrapper {
                position: relative;
                border: 0;
                box-shadow: unset;
            }
        }
    }

    .multiselect--above .multiselect__content-wrapper {
        bottom: calc(100% + #{$input-height});
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.1s;
    }
    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }

    .multiselect__spinner {
        position: absolute;
        right: 1px;
        top: 50%;
        width: 34px;
        height: 34px;
        background: #fff;
        display: block;
        transform: translateY(-50%);
    }

    .multiselect--disabled.multiselect--active {
        opacity: 1;
    }
</style>
